/** Modern hash-routed management shell embedded in the isolated DNN module. */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { HashRouter, Link, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import type { BootstrapVersion } from '../../app/runtime-config';
import { useUiLocale } from '../../app/ui-locale';
import { DynamicFormsPermission } from '../../models/access-control';
import { AccessControlPage } from '../access/AccessControlPage';
import { AccessProvider, AccessRoute, useAccess } from '../access/AccessContext';
import { FormDesignerPage } from '../designer/FormDesignerPage';
import { PublicFormApp } from '../renderer/PublicFormApp';
import { FormListPage } from '../forms/FormListPage';
import { ReportsListPage } from '../reports/ReportsListPage';
import { ReportDesignerPage } from '../reports/ReportDesignerPage';
import { SubmissionsPage } from '../submissions/SubmissionsPage';
import { AdminIcon, type AdminIconName } from './AdminIcon';
import { adminCopy } from './admin-copy';
import { FeaturePackagesPage } from './FeaturePackagesPage';
import { PortalCapabilitiesPage } from './PortalCapabilitiesPage';
import { SmsSettingsPage } from './SmsSettingsPage';
import { useManagementDomI18n } from './management-dom-i18n';

interface NavItem {
  to: string;
  match: string;
  labelKey: 'dashboard' | 'designer' | 'submissions' | 'reports' | 'settings' | 'access' | 'packages' | 'portalFeatures' | 'smsSettings';
  descriptionKey: 'dashboardDesc' | 'designerDesc' | 'submissionsDesc' | 'reportsDesc' | 'settingsDesc' | 'accessDesc' | 'packagesDesc' | 'portalFeaturesDesc' | 'smsSettingsDesc';
  icon: AdminIconName;
  visible: (access: ReturnType<typeof useAccess>['access']) => boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/dynamicforms/dashboard/10/1', match: '/dynamicforms/dashboard', labelKey: 'dashboard', descriptionKey: 'dashboardDesc', icon: 'dashboard', visible: (access) => access.CanAccessDashboard },
  { to: '/dynamicforms/forms/add', match: '/dynamicforms/forms', labelKey: 'designer', descriptionKey: 'designerDesc', icon: 'designer', visible: (access) => access.CanCreateForms || access.CanEditAnyForm },
  { to: '/dynamicforms/filters/list', match: '/dynamicforms/filters', labelKey: 'submissions', descriptionKey: 'submissionsDesc', icon: 'submissions', visible: (access) => access.CanViewAnySubmissions },
  { to: '/dynamicforms/reports/customreporstlist', match: '/dynamicforms/reports', labelKey: 'reports', descriptionKey: 'reportsDesc', icon: 'reports', visible: (access) => access.CanViewAnyReports },
  { to: '/dynamicforms/settings/portal-features', match: '/dynamicforms/settings/portal-features', labelKey: 'portalFeatures', descriptionKey: 'portalFeaturesDesc', icon: 'settings', visible: (access) => access.CanManagePortalFeatures },
  { to: '/dynamicforms/settings/sms', match: '/dynamicforms/settings/sms', labelKey: 'smsSettings', descriptionKey: 'smsSettingsDesc', icon: 'sms', visible: (access) => access.CanManageSmsSettings },
  { to: '/dynamicforms/settings/packages', match: '/dynamicforms/settings/packages', labelKey: 'packages', descriptionKey: 'packagesDesc', icon: 'forms', visible: (access) => access.CanManagePackages },
  { to: '/dynamicforms/settings/access', match: '/dynamicforms/settings/access', labelKey: 'access', descriptionKey: 'accessDesc', icon: 'access', visible: (access) => access.CanManageAccess }
];

function routeMeta(pathname: string, copy: ReturnType<typeof adminCopy>) {
  if (pathname.startsWith('/dynamicforms/forms/add')) return { title: copy.newFormTitle, eyebrow: copy.formBuilder };
  if (pathname.startsWith('/dynamicforms/forms/edit')) return { title: copy.editFormTitle, eyebrow: copy.formBuilder };
  if (pathname.startsWith('/dynamicforms/filters')) return { title: copy.submissionsTitle, eyebrow: copy.dataManagement };
  if (pathname.startsWith('/dynamicforms/reports/add')) return { title: copy.newReportTitle, eyebrow: copy.reportBuilder };
  if (pathname.startsWith('/dynamicforms/reports/edit')) return { title: copy.editReportTitle, eyebrow: copy.reportBuilder };
  if (pathname.startsWith('/dynamicforms/reports')) return { title: copy.reportsTitle, eyebrow: copy.reportsManagement };
  if (pathname.startsWith('/dynamicforms/settings/access')) return { title: copy.accessTitle, eyebrow: copy.accessEyebrow };
  if (pathname.startsWith('/dynamicforms/settings/packages')) return { title: copy.packagesTitle, eyebrow: copy.packagesEyebrow };
  if (pathname.startsWith('/dynamicforms/settings/portal-features')) return { title: copy.portalFeaturesTitle, eyebrow: copy.portalFeaturesEyebrow };
  if (pathname.startsWith('/dynamicforms/settings/sms')) return { title: copy.smsSettingsTitle, eyebrow: copy.smsSettingsEyebrow };
  if (pathname.startsWith('/dynamicforms/settings')) return { title: copy.settingsTitle, eyebrow: copy.settingsEyebrow };
  return { title: copy.dashboardTitle, eyebrow: copy.dashboardEyebrow };
}

function AdminChrome({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { access } = useAccess();
  const { locale, direction, setLocale } = useUiLocale();
  const copy = adminCopy(locale);
  const isDesignerRoute = location.pathname.startsWith('/dynamicforms/forms/');
  const isDashboardRoute = location.pathname.startsWith('/dynamicforms/dashboard');
  const [mobileOpen, setMobileOpen] = useState(false);
  const routeContentRef = useRef<HTMLDivElement>(null);
  useManagementDomI18n(routeContentRef, locale, location.pathname);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const stored = window.localStorage.getItem('df-admin-sidebar-collapsed');
      return stored === null ? false : stored === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    setMobileOpen(false);
    if (isDesignerRoute) setCollapsed(true);
  }, [isDesignerRoute, location.pathname]);

  useEffect(() => {
    try { window.localStorage.setItem('df-admin-sidebar-collapsed', collapsed ? '1' : '0'); } catch { /* Storage is optional. */ }
  }, [collapsed]);

  const meta = useMemo(() => routeMeta(location.pathname, copy), [copy, location.pathname]);
  const shellClass = [
    'df-admin-shell',
    collapsed ? 'is-sidebar-collapsed' : '',
    mobileOpen ? 'is-mobile-nav-open' : '',
    isDesignerRoute ? 'is-designer-route' : '',
    locale === 'fa' ? 'is-persian' : locale === 'fr' ? 'is-french' : 'is-english'
  ].filter(Boolean).join(' ');
  const navItems = NAV_ITEMS.filter((item) => item.visible(access));

  return <div className={shellClass} dir={direction} lang={locale} data-admin-locale={locale} data-admin-design="modern">
    <button type="button" className="df-admin-mobile-overlay" aria-label={copy.closeMenu} onClick={() => setMobileOpen(false)} />

    <aside className="df-admin-sidebar" aria-label={copy.mainMenu}>
      <div className="df-admin-brand-row">
        <Link className="df-admin-brand" to="/dynamicforms/dashboard/10/1" title="DynamicForms">
          <span className="df-admin-brand-mark" aria-hidden="true"><i/><i/><i/><i/></span>
          <span className="df-admin-brand-copy"><strong>DynamicForms</strong><small>{copy.brandSubtitle}</small></span>
        </Link>
        <button type="button" className="df-admin-collapse-button" aria-label={collapsed ? copy.expandMenu : copy.collapseMenu} onClick={() => setCollapsed((value) => !value)}>
          <AdminIcon name={collapsed ? (direction === 'rtl' ? 'chevron-left' : 'chevron-right') : (direction === 'rtl' ? 'chevron-right' : 'chevron-left')} size={18}/>
        </button>
      </div>

      <nav className="df-admin-nav">
        <span className="df-admin-nav-caption">{copy.mainMenu}</span>
        {navItems.map((item) => {
          const active = location.pathname.startsWith(item.match);
          const label = copy[item.labelKey];
          const description = copy[item.descriptionKey];
          return <Link key={item.to} to={item.to} className={`df-admin-nav-item ${active ? 'is-active' : ''}`} title={collapsed ? label : undefined} aria-current={active ? 'page' : undefined}>
            <span className="df-admin-nav-icon"><AdminIcon name={item.icon}/></span>
            <span className="df-admin-nav-copy"><strong>{label}</strong><small>{description}</small></span>
            <span className="df-admin-nav-indicator" aria-hidden="true"/>
          </Link>;
        })}
      </nav>

      <div className="df-admin-sidebar-footer">
        <a className="df-admin-site-link" href="/" title={collapsed ? copy.backToSite : undefined}>
          <span className="df-admin-nav-icon"><AdminIcon name="external"/></span>
          <span className="df-admin-nav-copy"><strong>{copy.backToSite}</strong><small>{copy.backToSiteDesc}</small></span>
        </a>
        <span className="df-admin-version">React 19 · v2.6</span>
      </div>
    </aside>

    <section className="df-admin-workspace">
      <header className="df-admin-topbar">
        <div className="df-admin-topbar-start">
          <button type="button" className="df-admin-mobile-menu" aria-label={copy.openMenu} aria-expanded={mobileOpen} onClick={() => setMobileOpen((value) => !value)}><AdminIcon name="menu"/></button>
          <div className="df-admin-page-context"><span>{meta.eyebrow}</span><strong>{meta.title}</strong></div>
        </div>
        <div className="df-admin-topbar-actions">
          <div className="df-admin-language-switch" role="group" aria-label={copy.language}>
            <AdminIcon name="language" size={17}/>
            <button type="button" className={locale === 'fa' ? 'is-active' : ''} onClick={() => setLocale('fa')} lang="fa" dir="rtl">فا</button>
            <button type="button" className={locale === 'en' ? 'is-active' : ''} onClick={() => setLocale('en')} lang="en" dir="ltr">EN</button>
            <button type="button" className={locale === 'fr' ? 'is-active' : ''} onClick={() => setLocale('fr')} lang="fr" dir="ltr">FR</button>
          </div>
          {access.CanCreateForms ? <Link className="df-admin-button df-admin-button--primary df-admin-button--compact" to="/dynamicforms/forms/add"><AdminIcon name="plus" size={18}/>{copy.newForm}</Link> : null}
        </div>
      </header>
      <main className="df-admin-main">
        <div ref={routeContentRef} className={`df-admin-route-content ${isDashboardRoute ? 'is-dashboard-content' : 'is-legacy-content'} ${isDesignerRoute ? 'is-designer-content' : ''}`} dir={direction}>{children}</div>
      </main>
    </section>
  </div>;
}

function DashboardRoute() {
  const { access } = useAccess();
  return access.CanAccessDashboard ? <FormListPage /> : <div className="alert alert-danger">اجازه مشاهده داشبورد را ندارید.</div>;
}

function AddFormRoute() {
  const { access } = useAccess();
  return access.CanCreateForms ? <AccessRoute permission={DynamicFormsPermission.CreateForms}><FormDesignerPage /></AccessRoute> : <div className="alert alert-danger">ایجاد فرم برای این پورتال مجاز نیست یا ظرفیت فرم تکمیل شده است.</div>;
}

function EditFormRoute() {
  const { id } = useParams();
  const { access } = useAccess();
  return access.CanEditAnyForm ? <AccessRoute permission={DynamicFormsPermission.EditForms} formId={Number(id)}><FormDesignerPage /></AccessRoute> : <div className="alert alert-danger">طراحی و ویرایش فرم برای این پورتال فقط توسط کاربر ارشد DNN مجاز است.</div>;
}

function SubmissionsRoute() {
  const { access } = useAccess();
  return access.CanViewAnySubmissions ? <SubmissionsPage /> : <div className="alert alert-danger">اجازه مشاهده پاسخ‌ها را ندارید.</div>;
}

function EditSubmissionRoute() {
  const { formid, submittedid } = useParams();
  const formId = Number(formid);
  const submittedFormId = Number(submittedid);
  if (!Number.isInteger(formId) || formId <= 0 || !Number.isInteger(submittedFormId) || submittedFormId <= 0) {
    return <div className="alert alert-danger">شناسه فرم یا پاسخ معتبر نیست.</div>;
  }
  return <AccessRoute permission={DynamicFormsPermission.ManageSubmissions} formId={formId}>
    <section className="df-submission-edit-page">
      <div className="df-submission-edit-page__toolbar"><Link className="btn btn-default" to="/dynamicforms/filters/list">بازگشت به پاسخ‌ها</Link></div>
      <PublicFormApp formId={formId} submittedFormId={submittedFormId} edit />
    </section>
  </AccessRoute>;
}

function ReportsRoute() {
  const { access } = useAccess();
  return access.CanViewAnyReports ? <ReportsListPage /> : <div className="alert alert-danger">اجازه مشاهده گزارش‌ها را ندارید.</div>;
}

function AddReportRoute() {
  const { formid } = useParams();
  return <AccessRoute permission={DynamicFormsPermission.ManageReports} formId={Number(formid)}><ReportDesignerPage /></AccessRoute>;
}

function EditReportRoute() {
  const { formid } = useParams();
  return <AccessRoute permission={DynamicFormsPermission.ManageReports} formId={Number(formid)}><ReportDesignerPage /></AccessRoute>;
}

function PackagesRoute() { const { access } = useAccess(); return access.CanManagePackages ? <FeaturePackagesPage /> : <div className="alert alert-danger">این بخش فقط برای کاربر ارشد DNN است.</div>; }
function PortalFeaturesRoute() { const { access } = useAccess(); return access.CanManagePortalFeatures ? <PortalCapabilitiesPage /> : <div className="alert alert-danger">این بخش فقط برای کاربر ارشد DNN است.</div>; }
function SmsSettingsRoute() { const { access } = useAccess(); return access.CanManageSmsSettings ? <SmsSettingsPage /> : <div className="alert alert-danger">اجازه مدیریت تنظیمات پیامک را ندارید.</div>; }

function AccessControlRoute() {
  const { access } = useAccess();
  return access.CanManageAccess ? <AccessControlPage /> : <div className="alert alert-danger">اجازه مدیریت سطوح دسترسی را ندارید.</div>;
}

function RoutesContent({ bootstrapVersion: _bootstrapVersion }: { bootstrapVersion: BootstrapVersion }) {
  return <AccessProvider><AdminChrome><Routes>
    <Route path="/dynamicforms/dashboard/:pagesize/:pageindex" element={<DashboardRoute />} />
    <Route path="/dynamicforms/forms/add" element={<AddFormRoute />} />
    <Route path="/dynamicforms/forms/edit/:id" element={<EditFormRoute />} />
    <Route path="/dynamicforms/filters/list" element={<SubmissionsRoute />} />
    <Route path="/dynamicforms/filters/edit/:formid/:submittedid" element={<EditSubmissionRoute />} />
    <Route path="/dynamicforms/reports/customreporstlist" element={<ReportsRoute />} />
    <Route path="/dynamicforms/reports/add/formid/:formid" element={<AddReportRoute />} />
    <Route path="/dynamicforms/reports/edit/:id/formid/:formid" element={<EditReportRoute />} />
    <Route path="/dynamicforms/settings/ui" element={<Navigate to="/dynamicforms/settings/portal-features" replace />} />
    <Route path="/dynamicforms/settings/packages" element={<PackagesRoute />} />
    <Route path="/dynamicforms/settings/portal-features" element={<PortalFeaturesRoute />} />
    <Route path="/dynamicforms/settings/sms" element={<SmsSettingsRoute />} />
    <Route path="/dynamicforms/settings/access" element={<AccessControlRoute />} />
    <Route path="*" element={<Navigate to="/dynamicforms/dashboard/10/1" replace />} />
  </Routes></AdminChrome></AccessProvider>;
}

export function ManagementApp({ editFormTabId, bootstrapVersion }: { editFormTabId?: string; bootstrapVersion: BootstrapVersion }) {
  return <HashRouter><RoutesContent bootstrapVersion={bootstrapVersion} /></HashRouter>;
}
